import { useMemo } from 'react';
import { Box, Flex, Menu, MenuButton, MenuItem, MenuList, Spinner, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { trimAddress } from '../../../utils/utils';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { useBlockchainProvider } from '../../../providers/Blockchain/blockchainProvider';
import { colors } from '../../../utils/colors';
import { DefaultAssets } from 'src/config';
import { useMoralisQuery } from 'react-moralis';
import { AvatarCircle } from './AvatarCircle/avatarCircle';

interface IGamerProfile {
	nickname: string;
	color: string;
	avatar: string;
	description: string;
	socials: string;
}

const defaultUser: IGamerProfile = {
	nickname: 'Unknown',
	color: '',
	avatar: DefaultAssets.defaultAvatar,
	description: '',
	socials: '',
};

export const AccountMini = () => {
	const { t } = useTranslation();
	const { account } = useBlockchainProvider();
	const { pathname } = useLocation();

	const { data, isLoading } = useMoralisQuery(
		'GamerProfile',
		query => query.equalTo('address', account.toLowerCase()),
		[account]
	);

	const userData: IGamerProfile = useMemo(() => {
		if (data.length > 0) {
			const payload = (data as any)[0].attributes;
			return {
				nickname: payload.nickname,
				avatar: payload.avatar,
				socials: payload.socials,
				color: payload.color,
				description: payload.description,
			};
		}
		return defaultUser;
	}, [data]);

	const nav = [
		{
			name: t('profileMenu.account'),
			url: `https://gamexplorer.io/user/${account.toLowerCase()}`,
		},
	];

	const menuNav = nav.map(({ url, name }) => (
		<MenuItem
			borderRadius="5px"
			bg={pathname === url ? 'cartridge.red' : 'field.background'}
			key={`acc_${name}_${url}`}
			onClick={() => window.location.replace(url)}
			_focus={{ bg: pathname === url ? 'cartridge.red' : 'field.background' }}
			_hover={{ bg: 'cartridge.red' }}
		>
			{name}
		</MenuItem>
	));

	const memoAvatar = useMemo(
		() => (
			<Box
				boxSize="34px"
				borderRadius="50%"
				bgSize="cover"
				bgImage={`url("${userData.avatar}")`}
			/>
		),
		[userData]
	);

	return (
		<Menu placement="bottom-end">
			<MenuButton>
				<Flex
					align="center"
					gridGap="13px"
				>
					<Box
						textAlign="end"
						fontWeight="600"
					>
						<Text
							fontSize="13px"
							color="white"
						>
							{isLoading ? userData.nickname : 'User'}
						</Text>
						<Text
							fontSize="11px"
							color={colors.liteGray}
						>
							{trimAddress(account)}
						</Text>
					</Box>
					<Flex
						cursor="pointer"
						pos="relative"
						color="black"
						fontWeight="bold"
						fontSize="12px"
						align="center"
						justify="center"
					>
						<AvatarCircle>{memoAvatar}</AvatarCircle>

						<Flex
							boxSize="15px"
							bottom="-1px"
							justify="center"
							align="center"
							bg="white"
							right="5px"
							borderRadius="50%"
							pos="absolute"
						>
							<ChevronDownIcon color="red" />
						</Flex>
					</Flex>
				</Flex>
			</MenuButton>

			<MenuList
				maxW="100px"
				p="0"
				border={`1px solid ${'cartridge.red'}`}
			>
				{menuNav}
			</MenuList>
		</Menu>
	);
};
